import { useState } from 'react';
import { useGo, useCustomMutation, useList } from '@refinedev/core';
import { Loader2, Webhook, Copy, Check, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { FormHeader } from '@/components/forms';
import type { TagItem } from '../../tags/types';
import { TagCreateDialog } from '../../tags/components';

export function LeadWebhooksCreate() {
  const go = useGo();
  const [name, setName] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [fieldMappingRaw, setFieldMappingRaw] = useState('{}');
  const [fieldMappingError, setFieldMappingError] = useState<string | null>(null);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [createdWebhookId, setCreatedWebhookId] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [createTagDialogOpen, setCreateTagDialogOpen] = useState(false);
  const [createdTags, setCreatedTags] = useState<TagItem[]>([]);

  const createMutation = useCustomMutation();

  const tagsResult = useList<TagItem>({
    resource: 'tags',
    pagination: { mode: 'off' },
  });
  const loadedTags = tagsResult.result?.data ?? [];
  const tagsById = new Map<number, TagItem>();
  for (const tag of [...createdTags, ...loadedTags]) {
    tagsById.set(tag.id, tag);
  }
  const tags = [...tagsById.values()];

  const validateFieldMapping = (raw: string): Record<string, string> | null => {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setFieldMappingError('Должен быть JSON объект { "ключ": "значение" }');
        return null;
      }
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof k !== 'string' || typeof v !== 'string') {
          setFieldMappingError('Все ключи и значения должны быть строками');
          return null;
        }
      }
      setFieldMappingError(null);
      return parsed as Record<string, string>;
    } catch {
      setFieldMappingError('Невалидный JSON');
      return null;
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) { return; }
    const fieldMapping = validateFieldMapping(fieldMappingRaw);
    if (fieldMapping === null) { return; }

    createMutation.mutate(
      {
        url: '/leads/webhooks',
        method: 'post',
        values: {
          name: name.trim(),
          defaultTagIds: selectedTagIds,
          fieldMapping,
          isActive: true,
        },
      },
      {
        onSuccess: (data) => {
          const result = data.data as { id: number; token?: string };
          setCreatedWebhookId(result.id);
          setCreatedToken(result.token ?? null);
          setTokenDialogOpen(true);
        },
      },
    );
  };

  const handleCopyToken = () => {
    if (!createdToken) { return; }
    void navigator.clipboard.writeText(createdToken).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleTokenDialogClose = () => {
    setTokenDialogOpen(false);
    if (createdWebhookId !== null) {
      go({ to: `/leads/webhooks/show/${createdWebhookId}` });
    }
  };

  const toggleTag = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  };

  return (
    <div className="space-y-4">
      <FormHeader
        title="Создать webhook"
        description="Настройка входящего webhook для автоматического создания лидов"
        onBack={() => go({ to: '/leads/webhooks' })}
        icon={<Webhook className="h-5 w-5" />}
        sticky
      />

      <div className="max-w-2xl space-y-4">
        {/* Basic info */}
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm">Основное</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-3">
            <div>
              <label htmlFor="webhook-name" className="text-sm text-muted-foreground mb-1.5 block">Название *</label>
              <Input
                id="webhook-name"
                placeholder="Название webhook (например: Форма с сайта)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Default tags */}
        <Card>
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-sm">Теги по умолчанию</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCreateTagDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Создать тег
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            <p className="text-xs text-muted-foreground mb-3">
              Эти теги будут автоматически добавляться к каждому лиду, созданному через этот webhook.
            </p>
            <div className="flex flex-wrap gap-2">
              {tags.length === 0 ? (
                <p className="text-sm text-muted-foreground">Нет доступных тегов</p>
              ) : (
                tags.map((tag) => {
                  const isSelected = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className="transition-opacity"
                    >
                      <Badge
                        variant={isSelected ? 'default' : 'outline'}
                        className="cursor-pointer text-xs px-2 py-0.5"
                        style={isSelected ? {} : { borderColor: tag.color, color: tag.color }}
                      >
                        {tag.name}
                      </Badge>
                    </button>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
        <TagCreateDialog
          open={createTagDialogOpen}
          onOpenChange={setCreateTagDialogOpen}
          title="Создать тег по умолчанию"
          description="Новый тег сразу будет выбран для этого webhook."
          successMessage="Тег создан и выбран"
          onCreated={(tag) => {
            setCreatedTags((prev) => [tag, ...prev.filter((item) => item.id !== tag.id)]);
            setSelectedTagIds((prev) => prev.includes(tag.id) ? prev : [...prev, tag.id]);
          }}
        />

        {/* Field mapping */}
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm">Маппинг полей</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-2">
            <p className="text-xs text-muted-foreground">
              JSON-объект: ключ — стандартное поле лида (email, phone, firstName, lastName, telegramUsername),
              значение — путь к полю в входящем JSON (поддерживает вложенность через &quot;.&quot;, например &quot;contact.email&quot;).
            </p>
            <textarea
              value={fieldMappingRaw}
              onChange={(e) => {
                setFieldMappingRaw(e.target.value);
                validateFieldMapping(e.target.value);
              }}
              className={`w-full min-h-[120px] font-mono text-xs p-3 rounded-md border bg-background resize-y focus:outline-none focus:ring-2 focus:ring-ring ${fieldMappingError ? 'border-destructive' : 'border-input'}`}
              spellCheck={false}
            />
            {fieldMappingError && (
              <p className="text-xs text-destructive">{fieldMappingError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Пример: {`{"email": "contact.email", "firstName": "user.name", "phone": "phone"}`}
            </p>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || createMutation.mutation.isPending || !!fieldMappingError}
          >
            {createMutation.mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Создать webhook
          </Button>
          <Button variant="outline" onClick={() => go({ to: '/leads/webhooks' })}>
            Отмена
          </Button>
        </div>
      </div>

      {/* Token dialog — shown only once on creation */}
      <Dialog open={tokenDialogOpen} onOpenChange={(open) => { if (!open) { handleTokenDialogClose(); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Webhook создан!</DialogTitle>
            <DialogDescription>
              Сохраните токен прямо сейчас — он больше не будет показан. Используйте его как часть URL для входящих запросов.
            </DialogDescription>
          </DialogHeader>

          {createdToken ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 bg-muted rounded-md p-3">
                <code className="flex-1 text-xs font-mono break-all">{createdToken}</code>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={handleCopyToken}>
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                URL для отправки данных: <code className="font-mono bg-muted px-1 rounded">/webhook/leads/{createdToken}</code>
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Токен недоступен. Пересоздайте webhook.</p>
          )}

          <DialogFooter>
            <Button onClick={handleTokenDialogClose}>
              Понял, сохранил токен
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
